import axios from "axios";
import { getApiBaseUrl, buildApiUrl } from "../config/api";

const TERM_SEQUENCE = ["Term 1", "Term 2", "Term 3"];

export const REQUIRED_SCHOOL_TERMS = TERM_SEQUENCE;

/** All three terms need open and closing dates before the calendar is considered configured. */
export function isTermCalendarConfigured(termDates) {
    const byTerm = new Map();
    for (const row of Array.isArray(termDates) ? termDates : []) {
        const term = String(row?.term || "").trim();
        if (term) byTerm.set(term, row);
    }
    return TERM_SEQUENCE.every((term) => {
        const row = byTerm.get(term);
        return Boolean(row?.openDate && row?.closeDate);
    });
}

/** Whether open and close dates have been saved for this term. */
export function termHasScheduleDates(term) {
    return Boolean(term?.openDate && term?.closeDate);
}

/**
 * UI label for term status. DB stores Planned | Open | Closed; "Planned" with no dates
 * means placeholders created with the academic year — show "Not set" so it is not confused
 * with a scheduled term.
 */
export function displayTermStatus(term) {
    const status = String(term?.status || "Planned");
    if (!termHasScheduleDates(term) && status === "Planned") {
        return { label: "Not set", color: "default", variant: "outlined" };
    }
    if (status === "Open") return { label: "Open", color: "success", variant: "outlined" };
    if (status === "Closed") return { label: "Closed", color: "default", variant: "outlined" };
    return { label: "Planned", color: "warning", variant: "outlined" };
}

export async function fetchTermConfigurationStatus({
    schoolId,
    schoolType,
    hasPrimaryClasses = true,
    hasSecondaryClasses = true,
}) {
    const needsPrimary =
        schoolType === "Primary" ||
        (schoolType === "Combined" && hasPrimaryClasses);
    const needsSecondary =
        schoolType === "Secondary" ||
        (schoolType === "Combined" && hasSecondaryClasses);

    if (!schoolId || schoolType === "University" || (!needsPrimary && !needsSecondary)) {
        return {
            primaryConfigured: true,
            secondaryConfigured: true,
            allConfigured: true,
        };
    }

    const checkYears = async (educationLevel) => {
        try {
            const { data: years } = await axios.get(buildApiUrl(`/AcademicPeriod/${schoolId}/years`));
            const list = Array.isArray(years) ? years : [];
            const scoped = list.filter(
                (y) => y.educationLevel === educationLevel || y.educationLevel === "All"
            );
            if (!scoped.length) return false;
            return scoped.some((y) =>
                TERM_SEQUENCE.every((termLabel) => {
                    const num = Number(String(termLabel).replace(/\D/g, ""));
                    const row = (y.terms || []).find((t) => t.termNumber === num || t.label === termLabel);
                    return Boolean(row?.openDate && row?.closeDate);
                })
            );
        } catch {
            return false;
        }
    };

    const loadLevelLegacy = async (educationLevel) => {
        for (const academicYear of academicYearsToCheck()) {
            try {
                const params = new URLSearchParams({
                    educationLevel,
                    academicYear,
                });
                const { data } = await axios.get(
                    `${getApiBaseUrl()}/SchoolCalendar/${schoolId}?${params.toString()}`
                );
                if (isTermCalendarConfigured(data?.termDates || [])) return true;
            } catch {
                /* try next academic year */
            }
        }
        return false;
    };

    const [primaryFromYears, secondaryFromYears] = await Promise.all([
        needsPrimary ? checkYears("Primary") : Promise.resolve(true),
        needsSecondary ? checkYears("Secondary") : Promise.resolve(true),
    ]);

    if (primaryFromYears && secondaryFromYears) {
        return { primaryConfigured: true, secondaryConfigured: true, allConfigured: true };
    }

    const [primaryConfigured, secondaryConfigured] = await Promise.all([
        needsPrimary ? (primaryFromYears || loadLevelLegacy("Primary")) : Promise.resolve(true),
        needsSecondary ? (secondaryFromYears || loadLevelLegacy("Secondary")) : Promise.resolve(true),
    ]);

    return {
        primaryConfigured,
        secondaryConfigured,
        allConfigured: primaryConfigured && secondaryConfigured,
    };
}

export function defaultAcademicYearMeta(date = new Date()) {
    const baseYear = date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1;
    return {
        label: `${baseYear}/${baseYear + 1}`,
        startYear: baseYear,
        endYear: baseYear + 1,
        calendarYear: String(date.getFullYear()),
    };
}

/** Current year plus adjacent options — matches the calendar page dropdown. */
export function academicYearsToCheck(date = new Date()) {
    const meta = defaultAcademicYearMeta(date);
    return [
        `${meta.startYear - 1}/${meta.startYear}`,
        meta.label,
        `${meta.endYear}/${meta.endYear + 1}`,
    ];
}

export function inferClassEducationLevel(className) {
    const value = String(className || "").trim();
    if (/^std\s*\d+/i.test(value)) return "Primary";
    if (/^form\s*\d+/i.test(value)) return "Secondary";
    return null;
}

function parseDateOnly(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Pick the term whose open/close window contains today, or the latest term that has started. */
export function resolveTermFromCalendar(termDates, today = new Date()) {
    const todayStart = parseDateOnly(today);
    if (!todayStart) return null;

    const rows = (Array.isArray(termDates) ? termDates : [])
        .filter((row) => String(row?.term || "").trim())
        .sort((a, b) => TERM_SEQUENCE.indexOf(a.term) - TERM_SEQUENCE.indexOf(b.term));

    if (!rows.length) return null;

    for (const row of rows) {
        const open = parseDateOnly(row.openDate);
        const close = parseDateOnly(row.closeDate);
        if (open && close && todayStart >= open && todayStart <= close) {
            return { term: row.term, source: "calendar" };
        }
    }

    let latestStarted = null;
    for (const row of rows) {
        const open = parseDateOnly(row.openDate);
        if (open && open <= todayStart) latestStarted = row;
    }
    if (latestStarted) {
        return { term: latestStarted.term, source: "calendar" };
    }

    return { term: rows[0].term, source: "calendar" };
}

export function resolveTermFromFeeStructures(feeStructures) {
    const active = (Array.isArray(feeStructures) ? feeStructures : []).filter((row) => row.isActive !== false);
    if (!active.length) return null;

    const counts = new Map();
    for (const row of active) {
        const term = String(row.term || "").trim();
        if (!term) continue;
        const academicYear = String(row.academicYear || "").trim();
        const key = `${term}|${academicYear}`;
        counts.set(key, (counts.get(key) || 0) + 1);
    }

    let best = null;
    let bestCount = 0;
    for (const [key, count] of counts.entries()) {
        if (count <= bestCount) continue;
        bestCount = count;
        const [term, academicYear] = key.split("|");
        best = { term, academicYear, source: "fees" };
    }
    return best;
}

function advanceTerm(term, academicYear) {
    const currentTerm = String(term || "").trim();
    const year = String(academicYear || "").trim();

    if (currentTerm === "Term 1") {
        return { term: "Term 2", academicYear: year, source: "activity" };
    }
    if (currentTerm === "Term 2") {
        return { term: "Term 3", academicYear: year, source: "activity" };
    }
    if (currentTerm === "Term 3") {
        const years = year.match(/\d{4}/g) || [];
        const nextYear = years.length ? String(Number(years[years.length - 1]) + 1) : year;
        return { term: "Term 1", academicYear: nextYear, source: "activity" };
    }
    return { term: currentTerm, academicYear: year, source: "activity" };
}

/** Infer working term from director review packages and recent end-of-term approvals. */
export function resolveTermFromActivity(bundles) {
    const list = Array.isArray(bundles) ? bundles : [];

    const submitted = list.filter((row) => row.status === "Submitted");
    if (submitted.length) {
        const latest = [...submitted].sort(
            (a, b) => new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime(),
        )[0];
        return {
            term: latest.term,
            academicYear: latest.academicYear,
            source: "activity",
        };
    }

    const approvedEndOfTerm = list.filter(
        (row) =>
            row.status === "Approved" &&
            String(row.reportPeriod || "").trim() === "End of term",
    );
    if (approvedEndOfTerm.length) {
        const latest = [...approvedEndOfTerm].sort(
            (a, b) =>
                new Date(b.publishedAt || b.reviewedAt || 0).getTime() -
                new Date(a.publishedAt || a.reviewedAt || 0).getTime(),
        )[0];
        return advanceTerm(latest.term, latest.academicYear);
    }

    const approved = list.filter((row) => row.status === "Approved");
    if (approved.length) {
        const latest = [...approved].sort(
            (a, b) =>
                new Date(b.publishedAt || b.reviewedAt || 0).getTime() -
                new Date(a.publishedAt || a.reviewedAt || 0).getTime(),
        )[0];
        return {
            term: latest.term,
            academicYear: latest.academicYear,
            source: "activity",
        };
    }

    return null;
}

export function formatCurrentTermLabel({ term, academicYear }) {
    const parts = [term, academicYear].filter(Boolean);
    return parts.join(" · ");
}

export async function fetchSchoolCurrentTerm({ schoolId, schoolType, feeStructures, includeFees = false }) {
    const yearMeta = defaultAcademicYearMeta();
    const educationLevel = schoolType === "Primary" ? "Primary" : "Secondary";

    let term = null;
    let academicYear = yearMeta.label;
    let source = "default";
    let termId = null;
    let academicYearId = null;

    if (schoolId) {
        try {
            const { data } = await axios.get(buildApiUrl(`/AcademicPeriod/${schoolId}/current`));
            if (data?.term?.label) {
                term = data.term.label;
                academicYear = data.academicYear?.label || academicYear;
                source = data.source || "period-api";
                termId = data.term?._id || data.term?.id || null;
                academicYearId = data.academicYear?._id || data.academicYear?.id || null;
            }
        } catch {
            /* fall through */
        }

        if (!term) {
            try {
                const params = new URLSearchParams({
                    educationLevel,
                    academicYear: yearMeta.label,
                });
                const { data } = await axios.get(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}?${params.toString()}`);
                const fromCalendar = resolveTermFromCalendar(data?.termDates || []);
                if (fromCalendar?.term) {
                    term = fromCalendar.term;
                    source = fromCalendar.source;
                }
            } catch {
                /* calendar optional */
            }
        }

        if (!term) {
            try {
                const { data: bundles } = await axios.get(`${getApiBaseUrl()}/ClassResults/school/${schoolId}`);
                const fromActivity = resolveTermFromActivity(bundles || []);
                if (fromActivity?.term) {
                    term = fromActivity.term;
                    academicYear = fromActivity.academicYear || academicYear;
                    source = fromActivity.source;
                }
            } catch {
                /* activity optional */
            }
        }
    }

    if (!term && includeFees) {
        const fromFees = resolveTermFromFeeStructures(feeStructures);
        if (fromFees?.term) {
            term = fromFees.term;
            academicYear = fromFees.academicYear || yearMeta.calendarYear;
            source = fromFees.source;
        }
    }

    if (!term) {
        term = schoolType === "University" ? "Semester 1" : "Term 1";
        academicYear = yearMeta.calendarYear;
        source = "default";
    }

    return {
        term,
        academicYear,
        termId,
        academicYearId,
        label: formatCurrentTermLabel({ term, academicYear }),
        source,
    };
}
